import { type ReactNode, useEffect, useId, useRef } from "react";
import { X } from "lucide-react";
import type { ResourceItem } from "@/data/content";
import { useAppTranslation } from "@/i18n/useAppTranslation";

type Props = {
  resource: ResourceItem;
  /** Price as shown on the card. */
  price: ReactNode;
  /** The card's own action (download or cart), repeated under the text. */
  action: ReactNode;
  onDismiss: () => void;
};

/** Native modal dialog with the cover at full size and the long description below it. */
export function ResourceDetailsModal({ resource: res, price, action, onDismiss }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const { t } = useAppTranslation("resourcesPage");
  const Icon = res.icon;
  const badge = res.badge?.trim() || res.type;
  const details = res.details?.trim() || res.desc;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => dialog?.close();
  }, []);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onDismiss();
      }}
      onClick={(e) => {
        if (e.target === dialogRef.current) onDismiss();
      }}
      className="m-auto max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-3xl overflow-y-auto overscroll-contain rounded-[1.75rem] bg-white p-0 text-ink shadow-lit-lg backdrop:bg-brand-primary/55 backdrop:backdrop-blur-sm"
    >
      <div className="relative">
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t("closeDetails")}
          className="absolute right-3 top-3 z-10 grid size-11 place-items-center rounded-full bg-white/90 text-ink-muted shadow-lit transition-colors hover:bg-teal-soft hover:text-brand-primary"
        >
          <X className="size-5" aria-hidden="true" />
        </button>
        <div className="bg-teal-soft">
          {res.coverSrc ? (
            <img
              src={res.coverSrc}
              alt={t("coverAlt", { title: res.title })}
              decoding="async"
              className="mx-auto block max-h-[70dvh] w-full object-contain"
            />
          ) : (
            <div className="light-pool grid aspect-[3/2] place-items-center [--pool-x:50%] [--pool-y:40%]" aria-hidden="true">
              <Icon className="size-20 text-teal-strong" strokeWidth={1.25} />
            </div>
          )}
        </div>
        <div className="p-7 sm:p-9">
          <span
            className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${
              res.badge?.trim() ? "bg-brand-primary text-white" : "bg-teal-soft text-teal-strong"
            }`}
          >
            {badge}
          </span>
          <h2 id={titleId} className="mt-4 font-display text-2xl font-semibold leading-snug text-brand-primary sm:text-3xl">
            {res.title}
          </h2>
          <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-ink-soft">{details}</p>
          <div className="mt-8 flex flex-wrap items-center justify-between gap-x-4 gap-y-3 border-t border-brand-primary/8 pt-6">
            <p className="font-display text-3xl font-semibold leading-none text-brand-primary">{price}</p>
            {action}
          </div>
        </div>
      </div>
    </dialog>
  );
}
